// Settings Page Component
// =======================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Button from '../ui/Button';
import Layout from '../layout/Layout';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('settings');
  const { user, logout, deleteAccount, changePassword, updateUser, isMockAuth } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit profile state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState(user?.displayName || '');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState(false);

  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // The word users must type to confirm deletion (language-dependent)
  const deleteWord = t('dangerZone.deleteAccount.confirmText');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== deleteWord) {
      setError(t('dangerZone.deleteAccount.confirmLabel'));
      return;
    }

    // For Firebase auth, password is required
    if (!isMockAuth && !deletePassword) {
      setError(t('dangerZone.deleteAccount.passwordLabel'));
      return;
    }

    setIsDeleting(true);
    setError(null);

    // Pass password for Firebase auth (undefined for mock auth)
    const result = await deleteAccount(isMockAuth ? undefined : deletePassword);

    if (result.success) {
      navigate('/login');
    } else {
      setError(result.error || t('dangerZone.deleteAccount.failed'));
      setIsDeleting(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(false);

    if (!currentPassword) {
      setPasswordError(t('account.changePassword.errors.currentRequired'));
      return;
    }

    if (!newPassword) {
      setPasswordError(t('account.changePassword.errors.newRequired'));
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError(t('account.changePassword.errors.tooShort'));
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError(t('account.changePassword.errors.dontMatch'));
      return;
    }

    setIsChangingPassword(true);

    const result = await changePassword(currentPassword, newPassword);

    if (result.success) {
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setShowChangePassword(false);
      // Show success briefly
      setTimeout(() => setPasswordSuccess(false), 5000);
    } else {
      setPasswordError(result.error || t('account.changePassword.errors.failed'));
    }

    setIsChangingPassword(false);
  };

  const handleUpdateProfile = async () => {
    setProfileError(null);
    setProfileSuccess(false);

    if (!editDisplayName.trim()) {
      setProfileError(t('profile.nameRequired'));
      return;
    }

    setIsUpdatingProfile(true);

    const result = await updateUser({ displayName: editDisplayName.trim() });

    if (result.success) {
      setProfileSuccess(true);
      setIsEditingProfile(false);
      setTimeout(() => setProfileSuccess(false), 5000);
    } else {
      setProfileError(result.error || t('profile.updateFailed'));
    }

    setIsUpdatingProfile(false);
  };

  const handleLanguageChange = async (lang: 'da' | 'en') => {
    i18n.changeLanguage(lang);
    localStorage.setItem('lyra-language', lang);
    if (user) {
      await updateUser({ preferences: { ...user.preferences, language: lang } });
    }
  };

  const dateLocale = i18n.language.startsWith('da') ? 'da-DK' : 'en-US';

  return (
    <Layout>
      <div className="bg-gradient-to-br from-white via-primary-50/30 to-indigo-50/20 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <main className="max-w-4xl mx-auto px-4 pb-8 space-y-6">
        {/* Header — part of the scrollable content flow to avoid phantom whitespace */}
        <header className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-white/50 dark:border-gray-700/50 -mx-4 px-4 py-4 flex items-center gap-4 sticky top-0 z-10">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors md:hidden"
            aria-label={t('backToDashboard')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-neutral-dark dark:text-white" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          {/* Feature #280: Serif display font for headings */}
          <h1 className="text-2xl font-display font-bold text-neutral-dark dark:text-white">
            {t('title')}
          </h1>
        </header>

        <div className="space-y-6">
        {/* Profile Section */}
        <section className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-neutral-dark dark:text-white">
              {t('profile.title')}
            </h2>
            {!isEditingProfile && (
              <Button variant="outline" size="sm" onClick={() => {
                setEditDisplayName(user?.displayName || '');
                setIsEditingProfile(true);
                setProfileError(null);
                setProfileSuccess(false);
              }}>
                {t('profile.edit')}
              </Button>
            )}
          </div>

          {profileSuccess && (
            <div
              className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
              role="status"
              aria-live="polite"
            >
              <p className="text-green-600 dark:text-green-400 text-sm font-medium">
                {t('profile.updated')}
              </p>
            </div>
          )}

          {isEditingProfile ? (
            <div className="space-y-4">
              {profileError && (
                <div
                  className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-600 dark:text-red-400 text-sm"
                  role="alert"
                >
                  {profileError}
                </div>
              )}
              <div>
                <label htmlFor="edit-display-name" className="block text-sm font-medium text-neutral-dark dark:text-gray-200 mb-1">
                  {t('profile.name')}
                </label>
                <input
                  id="edit-display-name"
                  type="text"
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-neutral-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={isUpdatingProfile}
                />
              </div>
              <div>
                <p className="text-sm text-neutral-medium">{t('profile.email')}</p>
                <p className="text-neutral-dark dark:text-white font-medium">
                  {user?.email || t('profile.notSpecified')}
                </p>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsEditingProfile(false);
                    setProfileError(null);
                  }}
                  disabled={isUpdatingProfile}
                >
                  {t('common:actions.cancel')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleUpdateProfile}
                  disabled={isUpdatingProfile}
                >
                  {isUpdatingProfile ? t('profile.saving') : t('profile.saveChanges')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <p className="text-sm text-neutral-medium">{t('profile.name')}</p>
                <p className="text-neutral-dark dark:text-white font-medium">
                  {user?.displayName || t('profile.notSpecified')}
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-medium">{t('profile.email')}</p>
                <p className="text-neutral-dark dark:text-white font-medium">
                  {user?.email || t('profile.notSpecified')}
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-medium">{t('profile.created')}</p>
                <p className="text-neutral-dark dark:text-white font-medium">
                  {user?.createdAt
                    ? new Date(user.createdAt).toLocaleDateString(dateLocale, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : t('profile.unknown')}
                </p>
              </div>
            </div>
          )}
        </section>

        {/* Adaptive Learning Section */}
        <section className="glass-card p-6">
          <h2 className="text-lg font-semibold text-neutral-dark dark:text-white mb-4">
            {t('adaptiveLearning.title')}
          </h2>
          <p className="text-sm text-neutral-medium mb-4">
            {t('adaptiveLearning.description')}
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-neutral-dark dark:text-white font-medium">{t('adaptiveLearning.knowledge.title')}</p>
                <p className="text-sm text-neutral-medium">{t('adaptiveLearning.knowledge.description')}</p>
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((level) => (
                  <div
                    key={level}
                    className={`w-3 h-3 rounded-full ${
                      level <= (user?.adaptiveLevels?.knowledge || 2)
                        ? 'bg-primary-500'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                    aria-hidden="true"
                  />
                ))}
                <span className="ml-2 text-sm font-medium text-neutral-dark dark:text-white">
                  {user?.adaptiveLevels?.knowledge || 2}/5
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-neutral-dark dark:text-white font-medium">{t('adaptiveLearning.language.title')}</p>
                <p className="text-sm text-neutral-medium">{t('adaptiveLearning.language.description')}</p>
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((level) => (
                  <div
                    key={level}
                    className={`w-3 h-3 rounded-full ${
                      level <= (user?.adaptiveLevels?.language || 2)
                        ? 'bg-secondary-500'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                    aria-hidden="true"
                  />
                ))}
                <span className="ml-2 text-sm font-medium text-neutral-dark dark:text-white">
                  {user?.adaptiveLevels?.language || 2}/5
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-neutral-dark dark:text-white font-medium">{t('adaptiveLearning.engagement.title')}</p>
                <p className="text-sm text-neutral-medium">{t('adaptiveLearning.engagement.description')}</p>
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((level) => (
                  <div
                    key={level}
                    className={`w-3 h-3 rounded-full ${
                      level <= (user?.adaptiveLevels?.engagement || 2)
                        ? 'bg-accent-500'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                    aria-hidden="true"
                  />
                ))}
                <span className="ml-2 text-sm font-medium text-neutral-dark dark:text-white">
                  {user?.adaptiveLevels?.engagement || 2}/5
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Appearance Section */}
        <section className="glass-card p-6">
          <h2 className="text-lg font-semibold text-neutral-dark dark:text-white mb-4">
            {t('appearance.title')}
          </h2>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-neutral-dark dark:text-white font-medium">{t('appearance.darkMode')}</p>
              <p className="text-sm text-neutral-medium">
                {isDarkMode ? t('appearance.darkModeEnabled') : t('appearance.lightModeEnabled')}
              </p>
            </div>
            <button
              onClick={toggleDarkMode}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                isDarkMode ? 'bg-primary-500' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={isDarkMode}
              aria-label={t('appearance.toggleLabel')}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  isDarkMode ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </section>

        {/* Language Section */}
        <section className="glass-card p-6">
          <h2 className="text-lg font-semibold text-neutral-dark dark:text-white mb-4">
            {t('languageSection.title')}
          </h2>
          <p className="text-sm text-neutral-medium mb-4">
            {t('languageSection.description')}
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => handleLanguageChange('da')}
              className={`px-4 py-2 rounded-lg border font-medium transition-colors ${
                i18n.language.startsWith('da')
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white dark:bg-gray-800 text-neutral-dark dark:text-white border-gray-300 dark:border-gray-600 hover:border-primary-300'
              }`}
            >
              Dansk
            </button>
            <button
              onClick={() => handleLanguageChange('en')}
              className={`px-4 py-2 rounded-lg border font-medium transition-colors ${
                i18n.language.startsWith('en')
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white dark:bg-gray-800 text-neutral-dark dark:text-white border-gray-300 dark:border-gray-600 hover:border-primary-300'
              }`}
            >
              English
            </button>
          </div>
        </section>

        {/* Account Section */}
        <section className="glass-card p-6">
          <h2 className="text-lg font-semibold text-neutral-dark dark:text-white mb-4">
            {t('account.title')}
          </h2>

          {passwordSuccess && (
            <div
              className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
              role="status"
              aria-live="polite"
            >
              <p className="text-green-600 dark:text-green-400 text-sm font-medium">
                {t('account.changePassword.success')}
              </p>
            </div>
          )}

          <div className="space-y-4">
            {/* Change Password */}
            <div className="py-3 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-neutral-dark dark:text-white font-medium">{t('account.changePassword.title')}</p>
                  <p className="text-sm text-neutral-medium">{t('account.changePassword.description')}</p>
                </div>
                {!showChangePassword && (
                  <Button variant="outline" size="sm" onClick={() => setShowChangePassword(true)}>
                    {t('account.changePassword.change')}
                  </Button>
                )}
              </div>

              {showChangePassword && (
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  {passwordError && (
                    <div
                      className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-600 dark:text-red-400 text-sm"
                      role="alert"
                    >
                      {passwordError}
                    </div>
                  )}

                  <div className="space-y-3">
                    <div>
                      <label htmlFor="current-password" className="block text-sm font-medium text-neutral-dark dark:text-gray-200 mb-1">
                        {t('account.changePassword.currentPassword')}
                      </label>
                      <input
                        id="current-password"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-neutral-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        disabled={isChangingPassword}
                      />
                    </div>
                    <div>
                      <label htmlFor="new-password" className="block text-sm font-medium text-neutral-dark dark:text-gray-200 mb-1">
                        {t('account.changePassword.newPassword')}
                      </label>
                      <input
                        id="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder={t('account.changePassword.newPasswordPlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-neutral-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        disabled={isChangingPassword}
                      />
                    </div>
                    <div>
                      <label htmlFor="confirm-new-password" className="block text-sm font-medium text-neutral-dark dark:text-gray-200 mb-1">
                        {t('account.changePassword.confirmNewPassword')}
                      </label>
                      <input
                        id="confirm-new-password"
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-neutral-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        disabled={isChangingPassword}
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowChangePassword(false);
                          setCurrentPassword('');
                          setNewPassword('');
                          setConfirmNewPassword('');
                          setPasswordError(null);
                        }}
                        disabled={isChangingPassword}
                      >
                        {t('common:actions.cancel')}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleChangePassword}
                        disabled={isChangingPassword}
                      >
                        {isChangingPassword ? t('account.changePassword.submitting') : t('account.changePassword.submit')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Logout */}
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-neutral-dark dark:text-white font-medium">{t('account.logout.title')}</p>
                <p className="text-sm text-neutral-medium">{t('account.logout.description')}</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                {t('account.logout.button')}
              </Button>
            </div>
          </div>
        </section>

        {/* Danger Zone */}
        <section className="glass-card border-red-200 dark:border-red-900 p-6">
          <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">
            {t('dangerZone.title')}
          </h2>
          <div className="space-y-4">
            <div>
              <p className="text-neutral-dark dark:text-white font-medium">{t('dangerZone.deleteAccount.title')}</p>
              <p className="text-sm text-neutral-medium mb-3">
                {t('dangerZone.deleteAccount.description')}
              </p>

              {!showDeleteConfirm ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
                >
                  {t('dangerZone.deleteAccount.button')}
                </Button>
              ) : (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-3">
                    {t('dangerZone.deleteAccount.confirmPrompt', {
                      passwordPrompt: !isMockAuth ? t('dangerZone.deleteAccount.passwordPrompt') : '',
                    })}
                  </p>

                  {error && (
                    <div
                      className="mb-3 p-2 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-red-600 dark:text-red-400 text-sm"
                      role="alert"
                    >
                      {error}
                    </div>
                  )}

                  <div className="space-y-3">
                    {/* Password field for Firebase auth */}
                    {!isMockAuth && (
                      <div>
                        <label htmlFor="delete-password" className="block text-sm font-medium text-red-600 dark:text-red-400 mb-1">
                          {t('dangerZone.deleteAccount.passwordLabel')}
                        </label>
                        <input
                          id="delete-password"
                          type="password"
                          value={deletePassword}
                          onChange={(e) => setDeletePassword(e.target.value)}
                          placeholder={t('dangerZone.deleteAccount.passwordPlaceholder')}
                          className="w-full px-3 py-2 border border-red-300 dark:border-red-700 rounded-lg bg-white dark:bg-gray-800 text-neutral-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                          disabled={isDeleting}
                        />
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="flex-1">
                        <label htmlFor="delete-confirm" className="sr-only">
                          {t('dangerZone.deleteAccount.confirmLabel')}
                        </label>
                        <input
                          id="delete-confirm"
                          type="text"
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                          placeholder={t('dangerZone.deleteAccount.confirmPlaceholder')}
                          className="w-full px-3 py-2 border border-red-300 dark:border-red-700 rounded-lg bg-white dark:bg-gray-800 text-neutral-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                          disabled={isDeleting}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowDeleteConfirm(false);
                            setDeleteConfirmText('');
                            setDeletePassword('');
                            setError(null);
                          }}
                          disabled={isDeleting}
                        >
                          {t('common:actions.cancel')}
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleDeleteAccount}
                          disabled={isDeleting}
                          className="bg-red-600 hover:bg-red-700 border-red-600"
                        >
                          {isDeleting ? t('dangerZone.deleteAccount.submitting') : t('dangerZone.deleteAccount.submit')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
        </div>
      </main>
      </div>
    </Layout>
  );
};

export default SettingsPage;
